import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import { formatISO, isAfter, startOfDay, sub } from "date-fns";

import { Page, ScreenHeader } from "@APP/components";
import { commonFormatDate } from "@APP/constants";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { hideLoader, showLoader } from "@APP/redux";
import { API } from "@APP/services";
import { filterToolbarStyles } from "@APP/styles";
import { AdminReusableRTPFilter, ReusableRTP } from "@APP/types";
import { formatErrorMessage } from "@APP/utils";

import ReusableRTPDetailDrawer from "./ReusableRTPDetailDrawer";
import ReusableRTPList from "./ReusableRTPList";

type DatePreset = "today" | "7d" | "30d" | "custom";

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

const EMAIL_PATTERN = /@/;

const presetRange = (preset: Exclude<DatePreset, "custom">) => {
  const now = new Date();
  const days = preset === "today" ? 0 : preset === "7d" ? 7 : 30;

  return { from: formatISO(startOfDay(sub(now, { days }))), to: formatISO(now) };
};

const defaultCustomFrom = () => new Date();
const defaultCustomTo = () => new Date();

const ReusableRTPDashboardView = () => {
  const classes = filterToolbarStyles();
  const alert = useAlert();
  const dispatch = useDispatch();
  const handleErrorCodes = useHandleErrorCodes();

  const [preset, setPreset] = useState<DatePreset>("today");
  const [customFrom, setCustomFrom] = useState<Date | null>(defaultCustomFrom);
  const [customTo, setCustomTo] = useState<Date | null>(defaultCustomTo);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [data, setData] = useState<ReusableRTP[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [entries, setEntries] = useState(25);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [selectedRtp, setSelectedRtp] = useState<ReusableRTP | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const lastPage = Math.max(1, Math.ceil(totalItems / entries));

  const handleView = (rtp: ReusableRTP) => {
    setSelectedRtp(rtp);
    setDrawerOpen(true);
  };

  // Resolves the active date window. Presets are resolved on the frontend
  // before hitting the endpoint, which only understands explicit from/to.
  const resolveRange = (): { from: string; to: string } | null => {
    if (preset !== "custom") return presetRange(preset);
    if (!customFrom || !customTo) return null;
    if (isAfter(customFrom, customTo)) return null;

    return { from: formatISO(customFrom), to: formatISO(customTo) };
  };

  const resolveSearchFilter = async (
    term: string,
  ): Promise<Pick<AdminReusableRTPFilter, "username" | "orgId">> => {
    const trimmed = term.trim();
    if (!trimmed) return {};
    if (EMAIL_PATTERN.test(trimmed)) return { username: trimmed };

    // Otherwise treat it as an organisation name and resolve it to every matching orgId.
    const orgs = await API.getSearchedOrganisations({ orgname: trimmed });
    const orgIds = orgs.data.map((org) => org.orgId);

    return orgIds.length ? { orgId: orgIds } : { username: trimmed };
  };

  const fetchPage = async (nextPage: number, nextEntries: number, term: string) => {
    const range = resolveRange();
    if (!range) {
      alert.open("Invalid date range", "The start date must be on or before the end date.");

      return;
    }

    try {
      setLoading(true);
      dispatch(showLoader());

      const searchFilter = await resolveSearchFilter(term);
      const filter: AdminReusableRTPFilter = { ...range, ...searchFilter };
      const { data: rtps, meta } = await API.getReusableRTPs(filter, {
        page: nextPage,
        entries: nextEntries,
      });

      setData(rtps);
      setTotalItems(meta?.totalItems ?? rtps.length);
    } catch (error: any) {
      const isHandled = handleErrorCodes(error?.response?.data?.errorCode);
      if (isHandled) alert.open("Error", formatErrorMessage(error));
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setLoaded(true);
      dispatch(hideLoader());
    }
  };

  // Initial load + reload whenever the date window changes (excluding custom,
  // which waits for an explicit Apply). Also resets the custom range so it
  // doesn't carry over stale values next time the user picks "Custom".
  useEffect(() => {
    if (preset === "custom") return;
    setCustomFrom(defaultCustomFrom());
    setCustomTo(defaultCustomTo());
    setPage(0);
    setSearchTerm("");
    setAppliedSearch("");
    fetchPage(0, entries, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const handleApplyCustom = () => {
    setPage(0);
    fetchPage(0, entries, appliedSearch);
  };

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setPage(0);
    fetchPage(0, entries, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setPage(0);
    fetchPage(0, entries, "");
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchPage(nextPage, entries, appliedSearch);
  };

  const handleEntriesChange = (nextEntries: number) => {
    setEntries(nextEntries);
    setPage(0);
    fetchPage(0, nextEntries, appliedSearch);
  };

  return (
    <Page title="Reusable RTP">
      <ScreenHeader title="Reusable RTP's" />

      <Box className={classes.toolbar}>
        <Box className={classes.topRow}>
          <div className={classes.segment} role="group" aria-label="Created date range">
            {PRESETS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={preset === key}
                className={`${classes.segmentButton} ${
                  preset === key ? classes.segmentButtonActive : ""
                }`}
                onClick={() => setPreset(key)}>
                {label}
              </button>
            ))}
          </div>

          <Box className={classes.searchGroup}>
            <TextField
              className={classes.search}
              size="small"
              placeholder="Search by username or organisation"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              inputProps={{ "aria-label": "Search reusable RTPs by username or organisation" }}
            />
            <Button variant="contained" color="primary" onClick={handleSearch}>
              Search
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearSearch}
              disabled={!searchTerm && !appliedSearch}>
              Clear
            </Button>
          </Box>
        </Box>

        {preset === "custom" && (
          <Box className={classes.customRange}>
            <DatePicker
              label="From"
              value={customFrom}
              inputFormat={commonFormatDate}
              disableFuture
              open={fromOpen}
              onOpen={() => setFromOpen(true)}
              onClose={() => setFromOpen(false)}
              onChange={setCustomFrom}
              renderInput={(props) => (
                <TextField
                  {...props}
                  size="small"
                  onClick={() => setFromOpen(true)}
                  error={Boolean(customFrom && customTo && isAfter(customFrom, customTo))}
                />
              )}
            />
            <DatePicker
              label="To"
              value={customTo}
              inputFormat={commonFormatDate}
              disableFuture
              open={toOpen}
              onOpen={() => setToOpen(true)}
              onClose={() => setToOpen(false)}
              onChange={setCustomTo}
              renderInput={(props) => (
                <TextField
                  {...props}
                  size="small"
                  onClick={() => setToOpen(true)}
                  helperText={
                    customFrom && customTo && isAfter(customFrom, customTo)
                      ? "Start date must be before end date"
                      : undefined
                  }
                  error={Boolean(customFrom && customTo && isAfter(customFrom, customTo))}
                />
              )}
            />
            <Button variant="contained" color="primary" onClick={handleApplyCustom}>
              Apply
            </Button>
          </Box>
        )}
      </Box>

      <ReusableRTPList
        data={data}
        loading={loading}
        loaded={loaded}
        page={page}
        entries={entries}
        lastPage={lastPage}
        onPageChange={handlePageChange}
        onEntriesChange={handleEntriesChange}
        onView={handleView}
      />

      <ReusableRTPDetailDrawer
        rtp={selectedRtp}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Page>
  );
};

export default ReusableRTPDashboardView;
